import { Link } from '@inertiajs/react'
import Layout from '../Layout'
import { formatCurrency } from '../../lib/format'

interface Account {
  id: number
  name: string
  slug: string
}

interface Variant {
  id: number
  sku: string
  price: number
  stock_quantity: number
  active: boolean
  option_display: string
  options_data?: Record<string, string>
}

interface Product {
  id: number
  name: string
  sku: string
  price: number
  cost_price: number
  stock_quantity: number
  description: string
  track_variants: boolean
  variant_count?: number
  total_stock?: number
  category?: { id: number; name: string }
  variants?: Variant[]
  promotions?: { name: string; tier_quantity: number; tier_price_cents: number }[]
}

interface Props {
  product: Product
  account: Account
  permissions?: {
    can_manage_products: boolean
  }
  show_costs?: boolean
}

export default function ProductsShow({ product, account, permissions, show_costs }: Props) {
  return (
    <Layout account={account} permissions={permissions}>
      <div className="max-w-3xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-[#202020]">{product.name}</h1>
            {product.track_variants && (
              <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-[#55ACEE] text-white">
                Variantes
              </span>
            )}
          </div>
          <Link
            href={`/${account.slug}/products/${product.id}/edit`}
            className="bg-[#55ACEE] text-white px-4 py-2 rounded hover:bg-[#4a9dd4] transition-colors"
          >
            Editar
          </Link>
        </div>

        {/* Product info */}
        <div className="bg-white p-6 rounded-lg shadow space-y-4">
          {(product.promotions ?? []).length > 0 && (
            <div>
              <p className="text-sm text-gray-500 mb-1.5">🏷️ Promociones aplicables</p>
              <div className="flex flex-wrap gap-2">
                {(product.promotions ?? []).map((p, i) => (
                  <span key={i} className="inline-flex items-center gap-1 bg-green-100 text-green-700 text-xs font-medium px-2 py-1 rounded">
                    {p.name} · {p.tier_quantity}u por {formatCurrency(p.tier_price_cents / 100)}
                  </span>
                ))}
              </div>
            </div>
          )}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">SKU</p>
              <p className="font-medium">{product.sku || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Categoría</p>
              <p className="font-medium">{product.category?.name || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Precio base</p>
              <p className="font-medium text-lg">{formatCurrency(Number(product.price))}</p>
            </div>
            {show_costs && (
              <div>
                <p className="text-sm text-gray-500">Costo</p>
                <p className="font-medium">{formatCurrency(Number(product.cost_price || 0))}</p>
              </div>
            )}
            {product.track_variants ? (
              <>
                <div>
                  <p className="text-sm text-gray-500">Total stock</p>
                  <p className={`font-medium text-lg ${
                    (product.total_stock || 0) < 10 ? 'text-red-600' : 'text-green-600'
                  }`}>
                    {product.total_stock || 0}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Nº de variantes</p>
                  <p className="font-medium">{product.variant_count || product.variants?.length || 0}</p>
                </div>
              </>
            ) : (
              <div>
                <p className="text-sm text-gray-500">Stock</p>
                <p className={`font-medium text-lg ${
                  product.stock_quantity < 10 ? 'text-red-600' : 'text-green-600'
                }`}>
                  {product.stock_quantity}
                </p>
              </div>
            )}
          </div>
          {product.description && (
            <div>
              <p className="text-sm text-gray-500">Descripción</p>
              <p className="font-medium">{product.description}</p>
            </div>
          )}
        </div>

        {/* Variants list */}
        {product.track_variants && product.variants && product.variants.length > 0 && (
          <div className="bg-white p-6 rounded-lg shadow mt-6 space-y-4">
            <h2 className="text-lg font-semibold text-[#202020]">
              Variantes ({product.variants.length})
            </h2>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Variante</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Precio</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Stock</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {product.variants.map((variant) => (
                    <tr key={variant.id}>
                      <td className="px-4 py-3">
                        <p className="font-medium text-[#202020]">{variant.option_display}</p>
                        {variant.options_data && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {Object.entries(variant.options_data).map(([key, value]) => (
                              <span key={key} className="inline-block bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded">
                                {key}: {value}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-500 font-mono text-sm">{variant.sku}</td>
                      <td className="px-4 py-3 font-medium">{formatCurrency(Number(variant.price))}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-sm ${
                          variant.stock_quantity < 10 ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                        }`}>
                          {variant.stock_quantity}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                          variant.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'
                        }`}>
                          {variant.active ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="mt-4">
          <Link
            href={`/${account.slug}/products`}
            className="text-[#55ACEE] hover:underline"
          >
            Volver a productos
          </Link>
        </div>
      </div>
    </Layout>
  )
}
